import React, { useState, useRef, useMemo, useEffect, useCallback, memo } from 'react';
import * as d1Api from '../../services/d1Api';
import { clampPct, getGuardZones, clampZonesToSafeStrip, invertRectToStrips, safeBottomPct, safeStripPct, menuZoneRect, punchMenuHole, mirrorX } from '../../utils/shieldZones';
import { Lock, VideoOff, CheckCircle, Maximize, Minimize } from 'lucide-react';

const TeleportingWatermark = memo(({ phone }) => {
    const [position, setPosition] = useState({ top: '10%', left: '10%' });
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        const interval = setInterval(() => {
            const randomTop = Math.floor(Math.random() * 80) + 5;
            const randomLeft = Math.floor(Math.random() * 80) + 5;
            setVisible(false);
            setTimeout(() => {
                setPosition({ top: `${randomTop}%`, left: `${randomLeft}%` });
                setVisible(true);
            }, 200);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div
            className="watermark-teleport"
            style={{
                top: position.top,
                left: position.left,
                opacity: visible ? 0.6 : 0,
                transition: 'opacity 0.2s ease',
            }}
        >
            <span>{phone.replace(/\s+/g, '')}</span>
        </div>
    );
});
TeleportingWatermark.displayName = 'TeleportingWatermark';

// Loads the YouTube IFrame Player API once and resolves when ready.
let ytApiPromise = null;
const loadYouTubeApi = () => {
    if (ytApiPromise) return ytApiPromise;
    ytApiPromise = new Promise((resolve) => {
        if (window.YT && window.YT.Player) return resolve(window.YT);
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof prev === 'function') prev();
            resolve(window.YT);
        };
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
    });
    return ytApiPromise;
};

const YoutubePlayer = memo(({ youtubeId, phone, onFsButton, isFs }) => {
    const hostRef = useRef(null);
    const playerRef = useRef(null);
    const [apiReady, setApiReady] = useState(false);
    // Admin-configured protection layer (from /settings/player_shield).
    const [shield, setShield] = useState({ enabled: true, x: 0, y: 0, w: 50, h: 55, opacity: 30, allowToggle: true, graceEnabled: true, graceSeconds: 10, graceX: 0, graceY: 0, graceW: 40, graceH: 30, showTimer: true, brandSize: 14, brandW: 100, brandH: 34, brandFsW: 100, brandFsH: 48, brandFsSize: 18, menuX: 58, menuW: 42, menuH: 55, menuY: null, yA: 0, yB: 0, xA: 0, xB: 0, fsBtnRight: 4, fsBtnBottom: 4, fsBtnW: 40, fsBtnH: 40, rtlMirror: false });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await d1Api.getSetting('player_shield');
                if (!cancelled && res?.success && res.data && typeof res.data === 'object') {
                    setShield((s) => ({ ...s, ...res.data }));
                }
            } catch {
                /* keep defaults */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Per-student mirror set by tech support (users.ui_mirror). OR-ed with the
    // global rtlMirror so support can flip ONE student's buttons on demand.
    const [personalMirror, setPersonalMirror] = useState(false);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const userId = localStorage.getItem('userId');
                if (!userId) return;
                const res = await d1Api.getProfile(userId);
                if (!cancelled && res?.success && res.data?.ui_mirror === 1) {
                    setPersonalMirror(true);
                }
            } catch {
                /* keep default */
            }
        })();
        return () => { cancelled = true; };
    }, []);
    const rtl = !!(shield.rtlMirror || personalMirror);
    const effShield = useMemo(() => ({ ...shield, rtlMirror: rtl }), [shield, rtl]);

    useEffect(() => {
        let cancelled = false;
        loadYouTubeApi().then((YT) => {
            if (cancelled || !hostRef.current) return;
            playerRef.current = new YT.Player(hostRef.current, {
                width: '100%',
                height: 400,
                videoId: youtubeId,
                playerVars: {
                    rel: 0,
                    iv_load_policy: 3,
                    playsinline: 1,
                    controls: 1,
                    modestbranding: 1,
                    enablejsapi: 1,
                    origin: window.location.origin,
                    // Quality menu must stay available for the student.
                    vq: 'default',
                },
                events: {
                    onReady: (e) => {
                        // The API writes a literal height attribute, which breaks
                        // the aspect ratio. Force the iframe to fill its box.
                        const frame = e.target.getIframe?.();
                        if (frame) {
                            frame.style.position = 'absolute';
                            frame.style.top = '0';
                            frame.style.left = '0';
                            frame.style.width = '100%';
                            frame.style.height = '100%';
                            frame.style.border = 'none';
                        }
                        setApiReady(true);
                    },
                    // Track playing state: the brand bar auto-hide runs only
                    // while the video is actually playing (paused = always shown).
                    onStateChange: (e) => {
                        try { setIsPlaying(e?.data === 1); } catch {}
                    },
                },
            });
        });
        return () => {
            cancelled = true;
            try { playerRef.current?.destroy(); } catch (e) {}
            playerRef.current = null;
        };
    }, [youtubeId]);

    const togglePlayback = useCallback(() => {
        const player = playerRef.current;
        if (!player || typeof player.getPlayerState !== 'function') return;
        try {
            if (player.getPlayerState() === 1) player.pauseVideo();
            else player.playVideo();
        } catch (e) {}
    }, []);

    const handleShieldClick = useCallback(() => {
        if (!shield.allowToggle) return;
        togglePlayback();
    }, [togglePlayback, shield.allowToggle]);

    const alpha = clampPct(shield.opacity) / 100;
    const { zones, guide } = useMemo(() => getGuardZones(effShield), [effShield]);

    // Grace mode: clicking the open video area pauses protection for a few
    // seconds (countdown shown) so the student can pick quality/settings,
    // then protection comes back automatically.
    const [graceLeft, setGraceLeft] = useState(0);
    const graceOn = shield.graceEnabled !== false;
    const graceSecs = Math.min(60, Math.max(3, Number(shield.graceSeconds) || 10));
    const startGrace = useCallback(() => {
        if (!graceOn || !shield.enabled) return;
        setGraceLeft((prev) => (prev > 0 ? prev : graceSecs));
    }, [graceOn, graceSecs, shield.enabled]);
    useEffect(() => {
        if (graceLeft <= 0) return;
        const t = setTimeout(() => setGraceLeft((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [graceLeft]);
    useEffect(() => { setGraceLeft(0); }, [youtubeId]);
    const inGrace = graceLeft > 0;

    // Measure the frame so guard zones can be clipped above the reserved
    // bottom control strip (brand bar + YouTube control bar). This is what
    // guarantees the settings button and the whole control bar stay
    // clickable whatever the admin configures.
    const frameRef = useRef(null);
    const [frameH, setFrameH] = useState(0);
    useEffect(() => {
        const el = frameRef.current;
        if (!el) return;
        const update = () => setFrameH(el.clientHeight || 0);
        update();
        window.addEventListener('resize', update);
        document.addEventListener('fullscreenchange', update);
        return () => {
            window.removeEventListener('resize', update);
            document.removeEventListener('fullscreenchange', update);
        };
    }, []);
    const showTimer = shield.showTimer !== false;
    // Fullscreen gets its own brand-bar dimensions (admin-configurable).
    const fsMode = !!isFs;
    const brandSize = fsMode
        ? Math.min(30, Math.max(10, Number(shield.brandFsSize) || 18))
        : Math.min(26, Math.max(10, Number(shield.brandSize) || 14));
    const brandW = fsMode
        ? Math.min(100, Math.max(20, Number(shield.brandFsW) || 100))
        : Math.min(100, Math.max(20, Number(shield.brandW) || 100));
    const brandH = fsMode
        ? Math.min(96, Math.max(24, Number(shield.brandFsH) || 48))
        : Math.min(64, Math.max(24, Number(shield.brandH) || 34));
    // Reserved control strip = brand bar + ~48px YouTube control bar.
    // Brand-aware so a taller bar can never let guards cover the controls,
    // in normal mode or fullscreen.
    const stripPx = brandH + 48;
    const safeZones = useMemo(() => clampZonesToSafeStrip(zones, frameH, stripPx), [zones, frameH, stripPx]);

    // The always-open settings-menu hole (bottom-right, above the control
    // strip). YouTube's popup menus render here, so this rect is punched out
    // of EVERY overlay — catcher, guards and grace strips alike.
    const stripPct = safeStripPct(frameH, stripPx);
    const menuHole = useMemo(() => menuZoneRect(stripPct, effShield), [stripPct, effShield]);
    const openGuards = useMemo(() => punchMenuHole(safeZones, stripPct, effShield), [safeZones, stripPct, effShield]);

    // During grace the logic INVERTS: ONLY the admin-defined grace rect stays
    // open/interactive, and the four strips around it block everything else.
    // Mirrored too when RTL is on so player and preview stay identical.
    const graceView = useMemo(() => {
        const raw = { x: shield.graceX, y: shield.graceY, w: shield.graceW ?? 40, h: shield.graceH ?? 30 };
        const rect = rtl ? mirrorX({ x: clampPct(raw.x), y: clampPct(raw.y), w: clampPct(raw.w), h: clampPct(raw.h) }) : raw;
        return invertRectToStrips(rect, safeBottomPct(frameH, stripPx));
    }, [shield.graceX, shield.graceY, shield.graceW, shield.graceH, rtl, frameH, stripPx]);
    const openGraceStrips = useMemo(() => punchMenuHole(graceView.strips, stripPct, effShield), [graceView, stripPct, effShield]);
    // Catcher parts: whole video area above the control strip, minus the menu hole.
    const catcherParts = useMemo(() => punchMenuHole(
        [{ x: 0, y: 0, w: 100, h: 100 - stripPct }], stripPct, effShield
    ), [stripPct, shield]);
    const brandFull = brandW >= 100;
    // Auto-hide brand bar: fullscreen + landscape only. Any touch/mouse
    // activity shows it; 3s of no interaction hides it again. Portrait
    // phones and normal mode always keep it visible.
    const [isLandscape, setIsLandscape] = useState(() => window.innerWidth > window.innerHeight);
    useEffect(() => {
        const mq = window.matchMedia?.('(orientation: landscape)');
        const update = () => setIsLandscape(mq?.matches ?? window.innerWidth > window.innerHeight);
        update();
        mq?.addEventListener?.('change', update);
        window.addEventListener('resize', update);
        return () => {
            mq?.removeEventListener?.('change', update);
            window.removeEventListener('resize', update);
        };
    }, []);
    const [isPlaying, setIsPlaying] = useState(false);
    useEffect(() => { setIsPlaying(false); }, [youtubeId]);
    const autoHideBrand = fsMode && isLandscape && isPlaying;
    const [brandVisible, setBrandVisible] = useState(true);
    const brandTimer = useRef(null);
    const pokeBrand = useCallback(() => {
        if (!(isFs && isLandscape && isPlaying)) return;
        setBrandVisible(true);
        if (brandTimer.current) clearTimeout(brandTimer.current);
        brandTimer.current = setTimeout(() => setBrandVisible(false), 5000);
    }, [isFs, isLandscape, isPlaying]);
    useEffect(() => {
        if (isFs && isLandscape && isPlaying) {
            setBrandVisible(true);
            if (brandTimer.current) clearTimeout(brandTimer.current);
            brandTimer.current = setTimeout(() => setBrandVisible(false), 5000);
        } else {
            if (brandTimer.current) clearTimeout(brandTimer.current);
            setBrandVisible(true);
        }
        return () => { if (brandTimer.current) clearTimeout(brandTimer.current); };
    }, [isFs, isLandscape, isPlaying]);
    useEffect(() => () => { if (brandTimer.current) clearTimeout(brandTimer.current); }, []);
    const brandShown = !autoHideBrand || brandVisible;
    // Fullscreen pill geometry (px — YouTube's native button is fixed-size,
    // so pixels place our cover exactly over it on any screen).
    const fsBtnW = Math.min(80, Math.max(24, Number(shield.fsBtnW) || 40));
    const fsBtnH = Math.min(80, Math.max(24, Number(shield.fsBtnH) || 40));
    const fsBtnRight = Math.min(120, Math.max(0, Number(shield.fsBtnRight ?? 4)));
    const fsBtnBottom = Math.min(120, Math.max(0, Number(shield.fsBtnBottom ?? 4)));
    const brandStyle = {
        fontSize: `${brandSize}px`,
        height: `${brandH}px`,
        ...(brandFull
            ? { left: 0, right: 0, transform: 'none' }
            : { left: '50%', right: 'auto', width: `${brandW}%`, transform: 'translateX(-50%)' }),
        opacity: brandShown ? 1 : 0,
        pointerEvents: brandShown ? 'auto' : 'none',
        transition: 'opacity 0.3s ease',
    };

    return (
        <div
            className="relative w-full h-full bg-black"
            onTouchStart={pokeBrand}
            onMouseDown={pokeBrand}
            onMouseMove={pokeBrand}
        >
            {/* Frame area reserves 34px at the bottom for the white brand bar,
                so YouTube's control bar stays fully visible and clickable. */}
            <div className="yt-frame-area" ref={frameRef} style={{ paddingBottom: `${brandH}px` }}>
                <div ref={hostRef} className="yt-player-mount w-full h-full" />

                {/* Cosmetic tint only: pointer-events:none, so every click
                    reaches YouTube. Darkness follows the admin's opacity. */}
                <div
                    className="yt-tint"
                    aria-hidden="true"
                    style={{ backgroundColor: `rgba(0,0,0,${alpha})` }}
                />

                {/* Grace catcher: invisible parts over the open video area
                    (above the iframe, below the guards, stopping above the
                    control strip, skipping the menu hole). One click on the
                    empty area starts the grace countdown; parts hide for the
                    whole grace period so clicks reach YouTube directly. */}
                {shield.enabled && graceOn && !inGrace && catcherParts.map((p, i) => (
                    <div
                        key={`c${i}`}
                        className="yt-catcher"
                        onClick={startGrace}
                        aria-hidden="true"
                        style={{
                            left: `${p.x}%`,
                            top: `${p.y}%`,
                            width: `${p.w}%`,
                            height: `${p.h}%`,
                            bottom: 'auto',
                        }}
                    />
                ))}

                {/* Normal protected zone(s), clipped above the reserved
                    control strip with the menu hole punched out. Hidden
                    during grace (replaced below). */}
                {shield.enabled && !inGrace && openGuards.map((z, i) => (
                    <div
                        key={i}
                        className="yt-guard"
                        onClick={handleShieldClick}
                        aria-hidden="true"
                        style={{
                            left: `${z.x}%`,
                            top: `${z.y}%`,
                            width: `${z.w}%`,
                            height: `${z.h}%`,
                            backgroundColor: `rgba(0,0,0,${alpha})`,
                        }}
                    />
                ))}

                {/* Grace mode: the four strips around the admin-defined grace
                    rect block everything (menu hole punched out); ONLY the
                    rect stays open so the student can use settings/quality
                    there. Blocked clicks do nothing at all. */}
                {shield.enabled && inGrace && openGraceStrips.map((z, i) => (
                    <div
                        key={`g${i}`}
                        className="yt-guard"
                        aria-hidden="true"
                        style={{
                            left: `${z.x}%`,
                            top: `${z.y}%`,
                            width: `${z.w}%`,
                            height: `${z.h}%`,
                            backgroundColor: `rgba(0,0,0,${alpha})`,
                            cursor: 'default',
                        }}
                    />
                ))}

                {/* Guide divider: visual only (pointer-events:none). Shown in
                    normal mode only — grace mode has its own layout. */}
                {shield.enabled && !inGrace && guide && (
                    <div
                        className="yt-guide"
                        aria-hidden="true"
                        style={guide.vertical
                            ? { left: `${guide.at}%`, top: 0, bottom: 0, borderLeft: '2px dashed rgba(255,255,255,0.55)' }
                            : { top: `${guide.at}%`, left: 0, right: 0, borderTop: '2px dashed rgba(255,255,255,0.55)' }}
                    />
                )}

                {/* Grace countdown badge (admin can hide it). */}
                {shield.enabled && inGrace && showTimer && (
                    <div className="yt-grace-badge" aria-hidden="true">
                        <span role="img" aria-label="hourglass">⏳</span>
                        <span>الجودة: {graceLeft}</span>
                    </div>
                )}

                <TeleportingWatermark phone={phone} />
                {!apiReady && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-slate-500 font-bold text-sm">جاري تحميل المشغل...</div>
                    </div>
                )}
            </div>

            {/* White brand bar pinned BELOW the player (admin-sized).
                It sits outside the guarded area, so it always toggles
                play/pause regardless of the allowToggle setting. */}
            <button
                type="button"
                onClick={togglePlayback}
                aria-label="تشغيل أو إيقاف الفيديو"
                aria-hidden={!brandShown}
                tabIndex={brandShown ? 0 : -1}
                className="yt-brand-bar"
                style={brandStyle}
            >
                سبيل التفوق
            </button>

            {/* Fullscreen pill: covers YouTube's native fullscreen button so
                only protected container fullscreen is reachable. Position and
                size are admin-configurable (px) to sit exactly over it.
                In fullscreen it sits right above the brand bar.
                Mirrored to the left when RTL is on (Arabic YouTube UI). */}
            <button
                type="button"
                onClick={onFsButton}
                aria-label="ملء الشاشة"
                title="ملء الشاشة"
                className="yt-fs-hijack"
                style={rtl
                    ? { bottom: `${brandH + (fsMode ? 0 : fsBtnBottom)}px`, left: `${fsBtnRight}px`, right: 'auto', width: `${fsBtnW}px`, height: `${fsBtnH}px` }
                    : { bottom: `${brandH + (fsMode ? 0 : fsBtnBottom)}px`, right: `${fsBtnRight}px`, width: `${fsBtnW}px`, height: `${fsBtnH}px` }}
            >
                ⛶
            </button>

            {/* Dead blocker squares under the native fullscreen spot, BOTH
                sides, same dims as the pill. No click handler — taps die here.
                So the native fullscreen stays unreachable even where our pill
                isn't covering (e.g. before support flips the buttons). */}
            <div
                className="yt-fs-blocker"
                aria-hidden="true"
                style={{ bottom: `${brandH + (fsMode ? 0 : fsBtnBottom)}px`, right: `${fsBtnRight}px`, width: `${fsBtnW}px`, height: `${fsBtnH}px` }}
            />
            <div
                className="yt-fs-blocker"
                aria-hidden="true"
                style={{ bottom: `${brandH + (fsMode ? 0 : fsBtnBottom)}px`, left: `${fsBtnRight}px`, width: `${fsBtnW}px`, height: `${fsBtnH}px` }}
            />
        </div>
    );
});
YoutubePlayer.displayName = 'YoutubePlayer';

export const LecturePlayer = ({ lecture, isLoading, studentPhone = "01xxxxxxxxx", isLocked = false, currentVideoPart = 0, setCurrentVideoPart = null }) => {
    const [isWatched, setIsWatched] = useState(false);
    const [isMarkingWatched, setIsMarkingWatched] = useState(false);
    const watchSecondsRef = useRef(0);
    const timerRef = useRef(null);

    const getYoutubeId = (url) => {
        if (!url || typeof url !== 'string') return null;
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^#&?]*)|([a-zA-Z0-9_-]{11})/);
        return (match && (match[1] || match[2])) ? (match[1] || match[2]) : null;
    };

    const parsedVideos = useMemo(() => {
        if (!lecture) return [];
        try {
            const parsed = typeof lecture.videos_list === 'string' ? JSON.parse(lecture.videos_list) : lecture.videos_list;
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        } catch (e) {}
        if (lecture.youtube_url || lecture.youtube_id || lecture.video_url) return [{ title: 'الجزء الرئيسي', url: lecture.youtube_url || lecture.youtube_id || lecture.video_url }];
        return [];
    }, [lecture]);

    const youtubeId = useMemo(() => getYoutubeId(parsedVideos[currentVideoPart]?.url || lecture?.video_url), [parsedVideos, currentVideoPart, lecture]);

    useEffect(() => {
        const checkWatchedStatus = async () => {
            if (!lecture?.id) return;
            const userId = localStorage.getItem('userId');
            if (!userId) return;
            try {
                const res = await d1Api.getCompletedAssignments(userId);
                if (res.success && res.data) {
                    const completedLectureIds = new Set(res.data.map(a => a.lecture_id));
                    if (completedLectureIds.has(lecture.id)) setIsWatched(true);
                }
            } catch (err) {}
        };
        checkWatchedStatus();
    }, [lecture?.id]);

    // Auto-mark: تقريبي - كل دقيقة مشاهدة مستمرة بتتراكم،
    // وبعد 5 دقايق مشاهدة فعليه بنعمل auto-mark (الحل الوحيد من غير enablejsapi)
    useEffect(() => {
        if (isWatched) return;
        watchSecondsRef.current = 0;
        timerRef.current = setInterval(() => {
            if (document.visibilityState === 'visible' && !document.hidden) {
                watchSecondsRef.current += 5;
                if (watchSecondsRef.current >= 300) {
                    clearInterval(timerRef.current);
                    markAsWatchedRef.current?.(true);
                }
            }
        }, 5000);
        return () => clearInterval(timerRef.current);
    }, [youtubeId, lecture?.id, isWatched]);

    const markAsWatched = useCallback(async (silent = false) => {
        if (isWatched || !lecture?.id) return;
        const userId = localStorage.getItem('userId');
        if (!userId) return;
        if (!silent) setIsMarkingWatched(true);
        try {
            const res = await d1Api.submitAssignment(userId, lecture.id);
            if (res.success) {
                setIsWatched(true);
                if (!silent) alert('تم تحديد المحاضرة كـ "تمت المشاهدة"! +10 نقاط');
            }
        } catch (err) {}
        finally { if (!silent) setIsMarkingWatched(false); }
    }, [isWatched, lecture?.id]);

    const markAsWatchedRef = useRef(markAsWatched);
    useEffect(() => { markAsWatchedRef.current = markAsWatched; });

    // Protected fullscreen: the whole player box (video + all overlays) goes
    // fullscreen, so every protection setting keeps working full-screen.
    const boxRef = useRef(null);
    const [isFs, setIsFs] = useState(false);
    useEffect(() => {
        const onFs = () => setIsFs(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onFs);
        return () => document.removeEventListener('fullscreenchange', onFs);
    }, []);
    const toggleFullscreen = useCallback(async () => {
        try {
            if (document.fullscreenElement) await document.exitFullscreen();
            else await boxRef.current?.requestFullscreen?.();
        } catch {}
    }, []);

    useEffect(() => {
        const preventDev = (e) => {
            if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key)) || (e.ctrlKey && e.key === 'U')) e.preventDefault();
        };
        const handleContext = (e) => e.preventDefault();
        document.addEventListener('keydown', preventDev);
        document.addEventListener('contextmenu', handleContext);
        return () => { document.removeEventListener('keydown', preventDev); document.removeEventListener('contextmenu', handleContext); };
    }, []);

    if (isLoading) return <div className="aspect-video bg-black rounded-[24px] flex items-center justify-center animate-pulse border border-white/10"><div className="text-slate-500 font-bold">جاري التحميل...</div></div>;
    if (isLocked) return <div className="aspect-video bg-gray-950 rounded-[24px] flex items-center justify-center border-2 border-brand-red/20"><Lock size={60} className="text-red-500" /></div>;
    if (!youtubeId) return <div className="aspect-video bg-gray-950 rounded-[24px] flex items-center justify-center"><VideoOff size={60} className="text-slate-500" /></div>;

    return (
        <div className="w-full flex flex-col" dir="rtl">
            {parsedVideos.length > 1 && (
                <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-2 backdrop-blur-sm">
                    <div className="flex gap-3 overflow-x-auto custom-scrollbar">
                        {parsedVideos.map((video, idx) => (
                            <button
                                key={idx}
                                onClick={() => { if (setCurrentVideoPart) setCurrentVideoPart(idx); }}
                                className={`px-5 py-2.5 rounded-full font-bold whitespace-nowrap transition-all duration-200 ${currentVideoPart === idx ? 'bg-red-600 text-white shadow-lg' : 'bg-[#1a1a1a] text-slate-400 hover:bg-[#2a2a2a] hover:text-white'}`}
                            >
                                {video.title || `الجزء ${idx + 1}`}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div ref={boxRef} className="relative w-full rounded-[24px] bg-black player-glow overflow-hidden">
                <YoutubePlayer
                    key={`yt-${youtubeId}-${currentVideoPart}`}
                    youtubeId={youtubeId}
                    phone={studentPhone}
                    onFsButton={toggleFullscreen}
                    isFs={isFs}
                />
            </div>

            <div className="mt-4 flex justify-center gap-3 flex-wrap">
                <button
                    onClick={() => markAsWatched(false)}
                    disabled={isWatched || isMarkingWatched}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all duration-200 ${
                        isWatched
                            ? 'bg-green-600/30 text-green-400 cursor-not-allowed border border-green-500/30'
                            : isMarkingWatched
                                ? 'bg-white/10 text-slate-400 cursor-wait border border-white/10'
                                : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500 shadow-lg hover:shadow-green-500/25'
                    }`}
                >
                    <CheckCircle size={20} />
                    {isWatched ? 'تمت المشاهدة' : 'تحديد كـ "تمت المشاهدة"'}
                </button>
                <button
                    onClick={toggleFullscreen}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all duration-200 bg-white/10 text-white border border-white/15 hover:bg-white/20 shadow-lg"
                >
                    {isFs ? <Minimize size={20} /> : <Maximize size={20} />}
                    {isFs ? 'خروج من ملء الشاشة' : 'ملء الشاشة'}
                </button>
            </div>

            <style>{`
                .player-glow { box-shadow: 0 0 40px 5px rgba(220, 38, 38, 0.15) !important; border: 1px solid rgba(255,255,255,0.05); }
                .player-glow:fullscreen { width: 100%; height: 100%; background: #000; border-radius: 0; }

                /* Player frame reserves room at the bottom for the white brand bar,
                   so YouTube's control bar is never hidden underneath it.
                   NOTE: padding-bottom has no !important — the admin-configured
                   brand height arrives via inline style and must win. */
                .yt-frame-area {
                    position: relative !important;
                    width: 100% !important;
                    height: 100% !important;
                    min-height: 400px !important;
                    padding-bottom: 34px;
                    box-sizing: border-box !important;
                }
                .yt-frame-area > .yt-player-mount { height: 100% !important; }

                /* White brand bar pinned BELOW the video.
                   NOTE: font-size/left/right/height have no !important — the
                   admin-configured size arrives via inline style and must win. */
                .yt-brand-bar {
                    position: absolute !important;
                    bottom: 0 !important;
                    left: 0;
                    right: 0;
                    height: 34px;
                    z-index: 2147483646 !important;
                    background: #ffffff !important;
                    color: #000000 !important;
                    font-weight: 800 !important;
                    font-size: 14px;
                    letter-spacing: 1px !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    cursor: pointer !important;
                    user-select: none !important;
                    border: none !important;
                    padding: 0 !important;
                    margin: 0 !important;
                    font-family: inherit !important;
                }
                .yt-brand-bar:active { background: #e5e5e5 !important; }

                /* Fullscreen pill: opaque button sitting over YouTube's native
                   fullscreen button, hiding it completely. Routes to protected
                   container fullscreen instead, so protection never drops.
                   Bottom offset follows the brand height via inline style. */
                .yt-fs-hijack {
                    position: absolute;
                    right: 4px;
                    width: 40px;
                    height: 40px;
                    z-index: 2147483646;
                    background: rgba(0, 0, 0, 0.85);
                    border: 1px solid rgba(255, 255, 255, 0.25);
                    border-radius: 10px;
                    padding: 0;
                    margin: 0;
                    cursor: pointer;
                    color: #fff;
                    font-size: 19px;
                    line-height: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .yt-fs-hijack:active { background: rgba(255, 255, 255, 0.25); }

                /* Dead fullscreen blockers: transparent squares over the native
                   fullscreen spot on BOTH sides. Below the pill in z-order so
                   our button still works on its side; taps anywhere else on
                   the native spot die silently. */
                .yt-fs-blocker {
                    position: absolute;
                    z-index: 2147483645;
                    background: transparent;
                    pointer-events: auto;
                    cursor: default;
                }

                /* Purely cosmetic darkening of the whole player.
                   pointer-events:none means it never swallows a click, so the
                   settings menu, the control bar and the video itself stay
                   fully usable at any player size.
                   NOTE: no !important here — the admin-configured opacity
                   arrives via inline style and must win. */
                .yt-tint {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    z-index: 2147483644;
                    background: rgba(0, 0, 0, 0.3);
                    pointer-events: none;
                }

                /* The admin-configured protected rectangle.
                   NOTE: no !important — geometry and darkness arrive via
                   inline style from /settings/player_shield. */
                .yt-guard {
                    position: absolute;
                    z-index: 2147483645;
                    background: rgba(0, 0, 0, 0.3);
                    cursor: pointer;
                }

                /* Guide divider between split guard zones.
                   Visual only — never captures clicks. */
                .yt-guide {
                    position: absolute;
                    z-index: 2147483646;
                    pointer-events: none;
                }

                /* Grace catcher: invisible, sits above the iframe but below
                   the guards and above nothing else that matters. Catches one
                   click on the open video area to start the grace countdown,
                   then hides so all clicks reach YouTube. Stops above the
                   82px control strip so the control bar is never covered. */
                .yt-catcher {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 82px;
                    z-index: 1000;
                    background: transparent;
                    cursor: pointer;
                }

                /* Grace countdown badge: tiny, top-left corner. Click-transparent. */
                .yt-grace-badge {
                    position: absolute;
                    top: 8px;
                    left: 8px;
                    z-index: 2147483646;
                    pointer-events: none;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    background: rgba(0, 0, 0, 0.75);
                    color: #fff;
                    font-weight: 800;
                    font-size: 11px;
                    padding: 4px 10px;
                    border-radius: 999px;
                    border: 1px solid rgba(255, 255, 255, 0.35);
                    white-space: nowrap;
                }

                .watermark-teleport {
                    position: absolute !important; z-index: 2147483647 !important;
                    pointer-events: none !important; user-select: none !important; display: block !important;
                }
                .watermark-teleport span {
                    display: block; white-space: nowrap; font-size: 18px !important; font-weight: 600 !important;
                    letter-spacing: 2px !important; color: rgba(220, 38, 38, 0.6) !important;
                    text-shadow: 0 0 3px rgba(0, 0, 0, 0.9) !important; line-height: 1;
                }
            `}</style>
        </div>
    );
};
